import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FileText, Upload, Search, Download, Trash2, Filter, X, Loader2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { format } from "date-fns";
import { toast } from "sonner";

const dealStages = [
  { value: "all", label: "Alle stages" },
  { value: "initial_contact", label: "Første kontakt" },
  { value: "qualification", label: "Kvalifisering" },
  { value: "pitch", label: "Pitch" },
  { value: "due_diligence", label: "Due Diligence" },
  { value: "negotiation", label: "Forhandling" },
  { value: "closing", label: "Avslutning" },
  { value: "closed_won", label: "Vunnet" },
  { value: "closed_lost", label: "Tapt" },
];

const fileTypes = [
  { value: "all", label: "Alle typer" },
  { value: "contract", label: "Kontrakt" },
  { value: "presentation", label: "Presentasjon" },
  { value: "report", label: "Rapport" },
  { value: "financial", label: "Finansiell" },
  { value: "legal", label: "Juridisk" },
  { value: "other", label: "Annet" },
];

export default function DocumentManager({ investor, activities = [] }) {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStage, setFilterStage] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [formData, setFormData] = useState({
    file: null,
    file_type: "other",
    deal_stage: "",
    activity_id: "",
    tags: "",
    description: "",
  });

  const queryClient = useQueryClient();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["documents", investor.id],
    queryFn: () => base44.entities.Document.filter({
      related_to_type: "investor",
      related_to_id: investor.id,
    }, "-created_date"),
  });

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Document.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success("Dokument slettet");
    },
  });

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!formData.file) return;

    setUploading(true);
    try {
      const uploadResult = await base44.integrations.Core.UploadFile({
        file: formData.file,
      });

      const tags = formData.tags.split(",").map(t => t.trim()).filter(Boolean);

      await base44.entities.Document.create({
        file_name: formData.file.name,
        file_url: uploadResult.file_url,
        file_size: formData.file.size,
        file_type: formData.file_type,
        uploaded_by: user.email,
        uploaded_by_name: user.full_name,
        related_to_type: "investor",
        related_to_id: investor.id,
        related_to_name: investor.name,
        deal_stage: formData.deal_stage || undefined,
        activity_id: formData.activity_id || undefined,
        tags: tags.length > 0 ? tags : undefined,
        description: formData.description || undefined,
      });

      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success("Dokument lastet opp");
      setShowUploadForm(false);
      setFormData({
        file: null,
        file_type: "other",
        deal_stage: "",
        activity_id: "",
        tags: "",
        description: "",
      });
    } catch (error) {
      toast.error("Kunne ikke laste opp dokumentet");
    } finally {
      setUploading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = 
      doc.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStage = filterStage === "all" || doc.deal_stage === filterStage;
    const matchesType = filterType === "all" || doc.file_type === filterType;
    return matchesSearch && matchesStage && matchesType;
  });

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Dokumenter</h3>
            <p className="text-sm text-slate-500">{documents.length} dokumenter</p>
          </div>
        </div>
        <Button onClick={() => setShowUploadForm(true)} size="sm">
          <Upload className="w-4 h-4 mr-2" />
          Last opp
        </Button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="mb-6 p-4 border-2 border-dashed border-lime-200 rounded-lg bg-lime-100/50">
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <Label htmlFor="file">Fil *</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                required
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="file_type">Type</Label>
                <Select value={formData.file_type} onValueChange={(v) => setFormData({ ...formData, file_type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fileTypes.slice(1).map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="deal_stage">Deal Stage (valgfri)</Label>
                <Select value={formData.deal_stage} onValueChange={(v) => setFormData({ ...formData, deal_stage: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Velg stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {dealStages.slice(1).map(stage => (
                      <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {activities.length > 0 && (
              <div>
                <Label htmlFor="activity_id">Koble til aktivitet (valgfri)</Label>
                <Select value={formData.activity_id} onValueChange={(v) => setFormData({ ...formData, activity_id: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Velg aktivitet" />
                  </SelectTrigger>
                  <SelectContent>
                    {activities.map(activity => (
                      <SelectItem key={activity.id} value={activity.id}>
                        {activity.title} - {format(new Date(activity.created_date), "dd.MM.yyyy")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="tags">Tags (kommaseparert)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="avtale, konfidensiell, 2024"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="description">Beskrivelse</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowUploadForm(false)}>
                Avbryt
              </Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Laster opp...
                  </>
                ) : (
                  "Last opp"
                )}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Søk dokumenter..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {dealStages.map(stage => (
              <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fileTypes.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Ingen dokumenter funnet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredDocuments.map(doc => (
            <div key={doc.id} className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-slate-600 flex-shrink-0" />
                    <h4 className="font-medium text-slate-900 truncate">{doc.file_name}</h4>
                  </div>
                  {doc.description && (
                    <p className="text-sm text-slate-600 mb-2">{doc.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>•</span>
                    <span>{format(new Date(doc.created_date), "dd.MM.yyyy")}</span>
                    {doc.file_type && doc.file_type !== "other" && (
                      <>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs">
                          {fileTypes.find(t => t.value === doc.file_type)?.label}
                        </Badge>
                      </>
                    )}
                    {doc.deal_stage && (
                      <>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs bg-lime-100 text-zinc-900 border-lime-200">
                          {dealStages.find(s => s.value === doc.deal_stage)?.label}
                        </Badge>
                      </>
                    )}
                  </div>
                  {doc.tags && doc.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.tags.map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(doc.file_url, "_blank")}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteMutation.mutate(doc.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}